import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Tool } from "@portfolio/types";
import { apiGet } from "../lib/api";
import { toolRegistry } from "../lib/toolRegistry";

function resolveIcon(name: string | null): LucideIcon {
  if (!name) return Icons.Wrench;
  const key = name.charAt(0).toUpperCase() + name.slice(1);
  const icon = (Icons as unknown as Record<string, LucideIcon>)[key];
  return icon ?? Icons.Wrench;
}

function hasPage(tool: Tool): boolean {
  // "live" trong DB không đủ — trang thật (registry) phải tồn tại thì mới cho vào.
  return tool.status === "live" && tool.slug in toolRegistry;
}

function SoonBadge() {
  return (
    <small className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-white/5 text-gray-500 border border-white/10">
      Sắp ra mắt
    </small>
  );
}

function FeaturedToolCard({ tool }: { tool: Tool }) {
  const Icon = resolveIcon(tool.icon);
  const clickable = hasPage(tool);

  const content = (
    <>
      <span className="icon-box w-14 h-14 rounded-2xl grid place-items-center bg-[#102744] text-[#9bc2ff]">
        <Icon size={26} />
      </span>
      <div className="mt-6">
        <b className="block text-xl font-bold">{tool.title}</b>
        <p className="text-gray-400 mt-2 leading-relaxed">{tool.description}</p>
        {!clickable && <SoonBadge />}
        {clickable && (
          <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[#8fb4ff]">
            Mở công cụ <Icons.ArrowRight size={15} />
          </span>
        )}
      </div>
    </>
  );

  const className =
    "glass-card flex flex-col h-full" + (clickable ? " cursor-pointer" : " opacity-60 cursor-not-allowed");

  if (!clickable) {
    return (
      <div className={className} aria-disabled="true">
        {content}
      </div>
    );
  }
  return (
    <Link to={`/tools/${tool.slug}`} className={className}>
      {content}
    </Link>
  );
}

function SmallToolCard({ tool }: { tool: Tool }) {
  const Icon = resolveIcon(tool.icon);
  const clickable = hasPage(tool);

  const content = (
    <>
      <span className="icon-box shrink-0 w-9 h-9 rounded-[9px] grid place-items-center bg-[#102744] text-[#9bc2ff]">
        <Icon size={16} />
      </span>
      <span>
        <b className="block font-semibold">{tool.title}</b>
        <small className="block text-gray-400 mt-1 leading-relaxed">{tool.description}</small>
        {!clickable && <SoonBadge />}
      </span>
    </>
  );

  const className =
    "small-tool flex items-start gap-3 p-5 rounded-2xl border border-white/10 bg-white/[0.03] transition-all" +
    (clickable ? " hover:-translate-y-0.5 hover:bg-white/5 cursor-pointer" : " opacity-60 cursor-not-allowed");

  if (!clickable) {
    return (
      <div className={className} aria-disabled="true">
        {content}
      </div>
    );
  }
  return (
    <Link to={`/tools/${tool.slug}`} className={className}>
      {content}
    </Link>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-4">
      <div className="h-56 rounded-2xl bg-white/[0.03] border border-white/10 animate-pulse" />
      <div className="flex flex-col gap-4">
        <div className="h-24 rounded-2xl bg-white/[0.03] border border-white/10 animate-pulse" />
        <div className="h-24 rounded-2xl bg-white/[0.03] border border-white/10 animate-pulse" />
      </div>
    </div>
  );
}

export default function ToolsSection() {
  const [tools, setTools] = useState<Tool[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiGet<Tool[]>("/api/v1/tools")
      .then((data) => {
        if (!cancelled) setTools(data ?? []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const header = (
    <div>
      <h2 className="text-2xl md:text-3xl font-bold">Công cụ nổi bật</h2>
      <p className="text-gray-400">Truy cập nhanh các tiện ích đang có trên website.</p>
    </div>
  );

  if (error) {
    return (
      <section id="tools" className="space-y-6 scroll-mt-24">
        {header}
        <p className="text-gray-400">Không tải được danh sách công cụ. Thử lại sau.</p>
      </section>
    );
  }

  if (tools === null) {
    return (
      <section id="tools" className="space-y-6 scroll-mt-24">
        {header}
        <LoadingSkeleton />
      </section>
    );
  }

  return (
    <section id="tools" className="space-y-6 scroll-mt-24">
      {header}
      {(() => {
        if (tools.length === 0) {
          return <p className="text-gray-500 text-sm">Chưa có công cụ nào.</p>;
        }
        // Tool đầu tiên (theo sort_order) hiện lớn bên trái; các tool còn lại
        // xếp dọc bên phải dạng ô nhỏ, giống bố cục preview.
        // Non-null: length === 0 đã return ở trên, nên tools[0] chắc chắn tồn tại.
        const featured = tools[0]!;
        const rest = tools.slice(1);
        return (
          <div
            className={rest.length > 0 ? "grid md:grid-cols-[1.1fr_0.9fr] gap-4 items-stretch" : "grid gap-4"}
          >
            <FeaturedToolCard tool={featured} />
            {rest.length > 0 && (
              <div className="flex flex-col gap-4">
                {rest.map((tool) => (
                  <SmallToolCard key={tool.id} tool={tool} />
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </section>
  );
}
